import { hasVoiceProof, VoiceProof } from './voice-proof';

const OPT_IN = 'mucka-native-voice-chat-v2';
export const nativeChatOptedIn = () => { try { return localStorage.getItem(OPT_IN) === '1'; } catch { return false; } };
export function setNativeChatOptIn(enabled: boolean) { localStorage.setItem(OPT_IN, enabled ? '1' : '0'); }
export interface NativeChatIdentity { allowed: boolean; enabled: boolean; accountId: string; userAuthId: string }
export async function nativeChatIdentity(options: { timeoutMs?: number } = {}): Promise<NativeChatIdentity> {
  // A fetch made as the signal drops does not fail, it waits out the phone's
  // own network timeout. A caller that cannot afford that passes a ceiling.
  const controller = options.timeoutMs ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), options.timeoutMs) : null;
  try {
    return await readIdentity(controller?.signal);
  } finally { if (timer) clearTimeout(timer); }
}

async function readIdentity(signal?: AbortSignal): Promise<NativeChatIdentity> {
  const response = await fetch('/api/voice/native-proof/eligibility', { cache: 'no-store', signal });
  if (!response.ok) throw new Error('Sign in again before using background voice.');
  return response.json();
}

/** Decide before admission. A failed native start never silently opens a second engine. */
export async function selectNativeChat(restoreOnly = false): Promise<boolean> {
  if (!hasVoiceProof()) return false;
  const snapshot = await VoiceProof.snapshot();
  const running = snapshot.state === 'active' || snapshot.state === 'connecting';
  if (running && snapshot.surface !== 'chat-v2') throw new Error('End the device test call before starting another voice conversation.');
  const version = await VoiceProof.capabilities().then(c => c.version).catch(() => 1);
  if (version < 2) return false;
  // Only the new store binaries default to native voice. Version-2 pilot
  // builds keep their explicit device opt-in; older apps and web stay as before.
  if (!running && (restoreOnly || (version < 3 && !nativeChatOptedIn()))) return false;
  const identity = await nativeChatIdentity();
  if (running) {
    if (!identity.allowed || snapshot.accountId !== identity.accountId || snapshot.userAuthId !== identity.userAuthId) {
      await VoiceProof.reset({ runtimeId: snapshot.runtimeId });
      throw new Error('The voice call belonged to a different workspace or sign-in and has ended.');
    }
    return true; // A kill switch stops new admissions, not this admitted call.
  }
  return identity.enabled;
}
